/* eslint-disable react/no-array-index-key */
import { ChangeEvent, useEffect, useState } from 'react'
import PlacesAutocomplete from 'react-places-autocomplete'
import { cn } from 'cn'

import TextField from 'components/ui/TextField'

export type Props = {
  name: string
  label?: string
  description?: string
  placeholder?: string
  error?: string | null
  disabled?: boolean
  value: string
  onChange: (value: string) => void
  newDesign?: boolean
}

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-script'
const GOOGLE_MAPS_SCRIPT_SRC =
  'https://maps.googleapis.com/maps/api/js?libraries=places&key=AIzaSyBYlsRTK-Jc-3X7jHyW7GvWT9iF0HKtlqY'

let googlePlacesLoaderPromise: Promise<void> | null = null

const isGooglePlacesReady = () =>
  typeof window !== 'undefined' && Boolean(window.google?.maps?.places)

const loadGooglePlacesScript = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve()
  }

  if (isGooglePlacesReady()) {
    return Promise.resolve()
  }

  if (googlePlacesLoaderPromise) {
    return googlePlacesLoaderPromise
  }

  googlePlacesLoaderPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(
      GOOGLE_MAPS_SCRIPT_ID
    ) as HTMLScriptElement | null
    const script = existingScript ?? document.createElement('script')

    const listeners = new AbortController()

    const handleLoad = () => {
      if (isGooglePlacesReady()) {
        listeners.abort()
        resolve()
        return
      }

      listeners.abort()
      googlePlacesLoaderPromise = null
      reject(new Error('Google Maps Places API loaded without places support'))
    }

    const handleError = () => {
      listeners.abort()
      googlePlacesLoaderPromise = null
      reject(new Error('Google Maps Places API failed to load'))
    }

    script.addEventListener('load', handleLoad, { signal: listeners.signal })
    script.addEventListener('error', handleError, { signal: listeners.signal })

    if (!existingScript) {
      script.src = GOOGLE_MAPS_SCRIPT_SRC
      script.id = GOOGLE_MAPS_SCRIPT_ID
      script.async = true
      document.body.appendChild(script)
    }

    if (isGooglePlacesReady()) {
      listeners.abort()
      resolve()
    }
  }).catch((error) => {
    googlePlacesLoaderPromise = null
    throw error
  })

  return googlePlacesLoaderPromise
}

export default function AddressField({
  label,
  description,
  placeholder,
  error,
  value,
  onChange,
  name,
  disabled,
  newDesign
}: Props) {
  const [text, setText] = useState<string>(value)
  const [placesStatus, setPlacesStatus] = useState<
    'loading' | 'ready' | 'error'
  >(() => (isGooglePlacesReady() ? 'ready' : 'loading'))

  useEffect(() => {
    setText(value)
  }, [value])

  useEffect(() => {
    let isMounted = true

    loadGooglePlacesScript()
      .then(() => {
        if (isMounted) {
          setPlacesStatus('ready')
        }
      })
      .catch(() => {
        if (isMounted) {
          setPlacesStatus('error')
        }
      })

    return () => {
      isMounted = false
    }
  }, [])

  const onSelect = (address: string) => {
    onChange(address)
  }

  const onBlur = () => {
    if (placesStatus === 'error') {
      onChange(text)
      return
    }

    setText(value)
  }

  const onManualChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value
    setText(nextValue)

    if (placesStatus === 'error') {
      onChange(nextValue)
    }
  }

  const searchOptions = {
    types: ['address'],
    componentRestrictions: { country: 'us' }
  }

  if (placesStatus !== 'ready') {
    return (
      <div className="relative">
        <TextField
          description={description}
          disabled={disabled}
          error={error}
          label={label}
          name={name}
          newDesign={newDesign}
          onBlur={onBlur}
          onChange={onManualChange}
          placeholder={placeholder}
          value={text}
        />
      </div>
    )
  }

  return (
    <PlacesAutocomplete
      onChange={setText}
      onSelect={onSelect}
      searchOptions={searchOptions}
      shouldFetchSuggestions
      value={text}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps }) => (
        <div className="relative">
          <TextField
            description={description}
            error={error}
            label={label}
            newDesign={newDesign}
            {...getInputProps({
              placeholder,
              onBlur
            })}
            disabled={disabled}
            name={name}
          >
            <div className="origin-top-right absolute left-0 mt-2 rounded-xl shadow-lg bg-white ring-1 ring-black/5 z-50">
              {suggestions.map((suggestion, index) => {
                const className = cn('py-1', {
                  'bg-orange-100 text-gray-900': suggestion.active
                })
                return (
                  <div
                    {...getSuggestionItemProps(suggestion, {
                      className
                    })}
                    key={`${suggestion.id}-${index}`}
                    aria-orientation="vertical"
                  >
                    <p className="block px-4 py-2 text-sm text-gray-700 hover:bg-orange-100 hover:text-gray-900 cursor-pointer">
                      {suggestion.description}
                    </p>
                  </div>
                )
              })}
            </div>
          </TextField>
        </div>
      )}
    </PlacesAutocomplete>
  )
}
